import {CustomerReferralProgramsPanel} from './CustomerReferralProgramsPanel';
import {useEffect,useRef,useState} from 'react';
import {ProfileAvatar} from './ProfileAvatar';
import {ProfileSavedCards} from './ProfileSavedCards';
import {CustomerPurchasesPanel} from './CustomerPurchasesPanel';
import {StaffSalePaymentReturn} from './StaffSalePaymentReturn';
import {StaffMembershipPaymentReturn} from './StaffMembershipPaymentReturn';
import {StaffGiftCardPaymentReturn} from './StaffGiftCardPaymentReturn';
import {ChangePasswordPanel} from './ChangePasswordPanel';
import {StaffLocationPermissions} from './StaffLocationPermissions';
import {AppSupportGrant} from './AppSupportGrant';

type TeamSummary={
  staffId:string;
  displayName:string;
  locationCodes:string[];
};
type Settings={version:number;contactEmail:string|null;phone:string|null;
  streetAddress:string|null;postalCode:string|null;birthday:string|null;
  defaultLocation:string|null;preferences:Record<string,boolean>;smsOptOut:boolean;
  autoDiscountId:number|null;singleDiscountId:number|null;
  autoDiscountTitle:string|null;singleDiscountTitle:string|null;
  referrerCustomerId:string|null;referrerName:string|null;shiftColor:string|null};
type CustomerProfileRecord={
  customerId:string;actorId:string|null;actorKind:'CUSTOMER'|'TEAM_MEMBER'|null;
  status:'ACTIVE'|'DISABLED';
  displayName:string;homeLocationCode:string|null;version:number;
  email:string|null;phoneE164:string|null;
  birthdayMonth:number|null;birthdayDay:number|null;birthdayYear:number|null;
  registrationDetails?:{address:string|null;postalCode:string|null;
    emergencyContactName:string|null;emergencyContactPhone:string|null;
    emergencyContactRelationship:string|null;heardFrom:string|null};
  emailPreferences?:{appointmentConfirmation:boolean;appointmentReminder:boolean;
    saleReceipt:boolean;nonEssential:boolean;mailingList:boolean}|null;
  emailConsent:string;smsConsent:string;smsAppointmentReminders?:boolean;
  sidebarSettings?:Settings|null;
};
type Profile=TeamSummary&{
  helmUserId?:string|null;email:string|null;lastLoginAt?:string|null;
  phone?:string|null;
  defaultLocation?:string|null;
  permissions:string[];settings:Settings;customerId?:string|null;
};
// Reopening the same person must await their pending in-tab autosaves.
const pendingProfileSaves=new Map<string,Promise<void>>();
const locationTitle:Record<string,string>={WC:'East Coast Float Spa West Chester',
  PR:'East Coast Float Spa Princeton',YD:'East Coast Float Spa Yardley'};
const profileTabs=[
  'Appointments','Service Credits','Memberships','Shares',
  'Purchases','Notes','Availability','Shifts','Tasks','Permissions',
] as const;
type Tab=typeof profileTabs[number];
type ProfileCredit={creditId:string;serviceCode:string;serviceName:string;
  remainingUses:number;issuedAt:string;expiresAt:string|null;
  availability:'available'|'unavailable'};
type CreditPage={availability:'available'|'unavailable';credits:ProfileCredit[]};
type CreditRead={state:'loading'|'ready'|'error'|'denied'|'unlinked';data:CreditPage|null};
type HistoryEntry={appointmentId:string;startsAt:string;serviceName:string;
  resourceName:string;status:string;locationTimezone:string};
type HistoryCursor={startsAt:string;appointmentId:string};
type HistoryPage={appointments:HistoryEntry[];hasMore:boolean;nextCursor:HistoryCursor|null};
type HistoryRead={state:'loading'|'ready'|'error'|'unlinked';
  items:HistoryEntry[];hasMore:boolean;nextCursor:HistoryCursor|null};
function validCreditPage(data:unknown,availability:CreditPage['availability']):
  data is CreditPage {
  if(!data||typeof data!=='object')return false;
  const page=data as Partial<CreditPage>;
  return page.availability===availability&&Array.isArray(page.credits)&&
    page.credits.every(c=>c&&typeof c.creditId==='string'&&
      typeof c.serviceCode==='string'&&typeof c.serviceName==='string'&&
      c.serviceName.trim().length>0&&Number.isSafeInteger(c.remainingUses)&&
      c.remainingUses>=0&&typeof c.issuedAt==='string'&&
      Number.isFinite(Date.parse(c.issuedAt))&&
      (c.expiresAt===null||(typeof c.expiresAt==='string'&&
        Number.isFinite(Date.parse(c.expiresAt))))&&c.availability===availability);
}
function validHistoryPage(data:unknown):data is HistoryPage {
  if(!data||typeof data!=='object')return false;
  const page=data as Partial<HistoryPage>;
  return Array.isArray(page.appointments)&&typeof page.hasMore==='boolean'&&
    (page.nextCursor===null||
      (typeof page.nextCursor?.startsAt==='string'&&
        Number.isFinite(Date.parse(page.nextCursor.startsAt))&&
        typeof page.nextCursor?.appointmentId==='string'))&&
    (!page.hasMore||!!page.nextCursor)&&
    page.appointments.every(x=>x&&typeof x.appointmentId==='string'&&
      typeof x.startsAt==='string'&&Number.isFinite(Date.parse(x.startsAt))&&
      typeof x.serviceName==='string'&&typeof x.resourceName==='string'&&
      typeof x.status==='string'&&typeof x.locationTimezone==='string');
}
function groupedCredits(credits:ProfileCredit[]){
  const grouped=new Map<string,{code:string;name:string;quantity:number}>();
  for(const entry of credits){
    const prior=grouped.get(entry.serviceCode);
    if(prior)prior.quantity+=entry.remainingUses;
    else grouped.set(entry.serviceCode,{code:entry.serviceCode,
      name:entry.serviceName,quantity:entry.remainingUses});
  }
  return [...grouped.values()].filter(x=>x.quantity>0);
}

/** Full-page employee profile based on the user-supplied Oct 27 FloatHelm
 * profile screenshots (Tom Korber, Grace Sherman), NOT a dialog.
 * Only real, permission-checked data is displayed; unimported customer
 * histories, credits and membership values must never be invented.
 */
export function TeamMemberProfileWorkspace({
  staffId,customerId,initial,locationCode,locationTimezone,onBack,onBeginSale,viewerActorId,canManageTeam=false,
}:{
  staffId:string;customerId?:string|null;
  initial?:TeamSummary|null;
  locationCode:string;locationTimezone?:string;
  onBack:()=>void;
  onBeginSale:(person:{customerId:string;displayName:string}|null)=>void;
  viewerActorId?:string;canManageTeam?:boolean;
}){
  const [profile,setProfile]=useState<Profile|null>(null);
  const [customerRecord,setCustomerRecord]=useState<CustomerProfileRecord|null>(null);
  // Queued autosaves must use the previous successful server state, never
  // the stale record captured when several checkboxes were clicked quickly.
  const customerRecordRef=useRef<CustomerProfileRecord|null>(null);
  const [appointmentHistory,setAppointmentHistory]=useState<HistoryRead>({
    state:'loading',items:[],hasMore:false,nextCursor:null,
  });
  const [historyBusy,setHistoryBusy]=useState(false);
  const [historyError,setHistoryError]=useState('');
  const historyRequest=useRef<AbortController|null>(null);
  const [creditAvailability,setCreditAvailability]=useState<'available'|'unavailable'>('available');
  const [creditRevision,setCreditRevision]=useState(0);
  const [creditExpiryId,setCreditExpiryId]=useState<string|null>(null);
  const [creditNeverExpires,setCreditNeverExpires]=useState(true);
  const [creditExpiresOn,setCreditExpiresOn]=useState('');
  const [creditExpiryBusy,setCreditExpiryBusy]=useState(false);
  const [creditExpiryStatus,setCreditExpiryStatus]=useState('');
  const [availableCredits,setAvailableCredits]=useState<CreditRead|null>(null);
  const [unavailableCredits,setUnavailableCredits]=useState<CreditRead|null>(null);
  const availableRequest=useRef<AbortController|null>(null);
  const unavailableRequest=useRef<AbortController|null>(null);
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [tab,setTab]=useState<Tab>('Appointments');
  const [appointmentStatusFilter,setAppointmentStatusFilter]=useState('All');
  const [draft,setDraft]=useState({firstName:'',lastName:'',contactEmail:'',
    phone:'',streetAddress:'',postalCode:'',birthday:'',defaultLocation:'',
    birthdayMonth:'',birthdayDay:'',birthdayYear:'',
    emergencyContactName:'',emergencyContactPhone:'',
    emergencyContactRelationship:'',heardFrom:'',
    preferences:{} as Record<string,boolean>,smsOptOut:false});
  const [saveStatus,setSaveStatus]=useState('');
  const [beginSaleBusy,setBeginSaleBusy]=useState(false);
  const [beginSaleError,setBeginSaleError]=useState('');
  const [employmentStatus,setEmploymentStatus]=useState('');
  const [employmentBusy,setEmploymentBusy]=useState(false);
  const [accountStatusBusy,setAccountStatusBusy]=useState(false);
  const [accountStatusNotice,setAccountStatusNotice]=useState('');
  const [optOutOpen,setOptOutOpen]=useState(false);
  const [passwordOpen,setPasswordOpen]=useState(false);
  const [discountOpen,setDiscountOpen]=useState<'auto'|'single'|null>(null);
  const [discountQuery,setDiscountQuery]=useState('');
  const [discountChoices,setDiscountChoices]=useState<Array<{
    id:number;title:string;code:string;description:string;channel:string}>>([]);
  const [discountStatus,setDiscountStatus]=useState('');
  const [referrerOpen,setReferrerOpen]=useState(false);
  const [referrerQuery,setReferrerQuery]=useState('');
  const [referrerChoices,setReferrerChoices]=useState<Array<{
    customerId:string;displayName:string;homeLocationCode:string|null}>>([]);
  const [referrerStatus,setReferrerStatus]=useState('');
  const version=useRef(1);
  const sidebarVersion=useRef(1);
  const [birthdayEditing,setBirthdayEditing]=useState(false);
  const [waiversOpen,setWaiversOpen]=useState(false);
  const [waiverHistory,setWaiverHistory]=useState<Array<{
    acceptanceId:string;title:string;revision:number;signedAt:string}>>([]);
  const [waiverStatus,setWaiverStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [waiverTotalCount,setWaiverTotalCount]=useState(0);
  const queue=useRef<Promise<void>>(Promise.resolve());
  const preferenceLabels=[
    ['mailingList','Mailing List'],
    ['nonEssentialEmails','Non-essential Emails'],
    ['saleReceipts','Email Sale Receipts'],
    ['emailConfirmations','Email Confirmations'],
    ['emailReminders','Email Reminders'],
    ['textAppointmentReminders','Text Appointment Reminders'],
  ] as const;

  useEffect(()=>{
    const controller=new AbortController();
    setTab('Appointments');
    setStatus('loading');
    setProfile(null);customerRecordRef.current=null;
    setSaveStatus('');setOptOutOpen(false);setBirthdayEditing(false);
    setWaiversOpen(false);setWaiverHistory([]);setWaiverTotalCount(0);
    setWaiverStatus('loading');
    historyRequest.current?.abort();availableRequest.current?.abort();
    unavailableRequest.current?.abort();
    setHistoryError('');setHistoryBusy(false);
    setCreditAvailability('available');setAvailableCredits(null);setUnavailableCredits(null);
    const params=new URLSearchParams({location:locationCode});
    const url=customerId
      ?'/api/v1/staff/customers/'+encodeURIComponent(customerId)
      :'/api/v1/staff/team/'+encodeURIComponent(staffId)+'/profile';
    const pendingKey=customerId?'customer:'+customerId:'staff:'+staffId;
    void Promise.resolve(pendingProfileSaves.get(pendingKey))
      .catch(()=>{})
      .then(()=>fetch(url+'?'+params.toString(),{
        signal:controller.signal,credentials:'same-origin',cache:'no-store',
      })).then(async response=>{
      if(!response.ok)throw new Error('PROFILE_UNAVAILABLE');
      const body=await response.json() as {
        ok:boolean;data?:{profile:Profile|CustomerProfileRecord};
      };
      if(!body.ok||!body.data?.profile)throw new Error('PROFILE_UNAVAILABLE');
      if(controller.signal.aborted)return;
      const raw=body.data.profile;
      let loaded:Profile;
      if(customerId){
        const person=raw as CustomerProfileRecord;
        if(person.customerId!==customerId)throw Error('PROFILE_UNAVAILABLE');
        setCustomerRecord(person);customerRecordRef.current=person;
        // Employee settings can predate the unified customer's saved registration.
        const prefs=person.emailPreferences;
        const fromActor=person.sidebarSettings?.preferences??{};
        const resolvedPreferences=prefs?{
          mailingList:prefs.mailingList,nonEssentialEmails:prefs.nonEssential,
          saleReceipts:prefs.saleReceipt,emailConfirmations:prefs.appointmentConfirmation,
          emailReminders:prefs.appointmentReminder,
        }:fromActor;
        loaded={
          staffId:person.actorId??customerId,displayName:person.displayName,
          customerId,locationCodes:person.homeLocationCode?
            [person.homeLocationCode]:[locationCode],
          email:person.email,phone:person.phoneE164,
          defaultLocation:person.homeLocationCode,permissions:[],
          settings:{version:person.version,contactEmail:person.email,
            phone:person.phoneE164,streetAddress:person.registrationDetails?.address??null,
            postalCode:person.registrationDetails?.postalCode??null,
            birthday:person.birthdayYear&&person.birthdayMonth&&person.birthdayDay
              ?[person.birthdayYear,String(person.birthdayMonth).padStart(2,'0'),
                String(person.birthdayDay).padStart(2,'0')].join('-'):null,
            defaultLocation:person.sidebarSettings?.defaultLocation??person.homeLocationCode,
            preferences:{...resolvedPreferences,
              textAppointmentReminders:person.smsAppointmentReminders===true||
                (fromActor.textAppointmentReminders===true&&person.smsConsent!=='OPTED_OUT')},
            smsOptOut:person.smsConsent==='OPTED_OUT'||
              person.sidebarSettings?.smsOptOut===true,
            autoDiscountId:person.sidebarSettings?.autoDiscountId??null,
            singleDiscountId:person.sidebarSettings?.singleDiscountId??null,
            autoDiscountTitle:person.sidebarSettings?.autoDiscountTitle??null,
            singleDiscountTitle:person.sidebarSettings?.singleDiscountTitle??null,
            referrerCustomerId:person.sidebarSettings?.referrerCustomerId??null,
            referrerName:person.sidebarSettings?.referrerName??null,shiftColor:null},
        };
      }else{
        loaded=raw as Profile;
        if(loaded.staffId!==staffId)throw Error('PROFILE_UNAVAILABLE');
        setCustomerRecord(null);customerRecordRef.current=null;
      }
      setProfile(loaded);
      version.current=loaded.settings?.version??1;
      sidebarVersion.current=customerId?
        (raw as CustomerProfileRecord).sidebarSettings?.version??1:version.current;
      const names=loaded.displayName.trim().split(/\s+/);
      setDraft({firstName:names[0]??'',lastName:names.slice(1).join(' '),
        contactEmail:loaded.email??'',phone:loaded.phone??'',
        streetAddress:loaded.settings?.streetAddress??'',
        postalCode:loaded.settings?.postalCode??'',birthday:loaded.settings?.birthday??'',
        birthdayMonth:customerId?String((raw as CustomerProfileRecord).birthdayMonth??''):'',
        birthdayDay:customerId?String((raw as CustomerProfileRecord).birthdayDay??''):'',
        birthdayYear:customerId?String((raw as CustomerProfileRecord).birthdayYear??''):'',
        emergencyContactName:customerId?
          (raw as CustomerProfileRecord).registrationDetails?.emergencyContactName??'':'',
        emergencyContactPhone:customerId?
          (raw as CustomerProfileRecord).registrationDetails?.emergencyContactPhone??'':'',
        emergencyContactRelationship:customerId?
          (raw as CustomerProfileRecord).registrationDetails?.emergencyContactRelationship??'':'',
        heardFrom:customerId?
          (raw as CustomerProfileRecord).registrationDetails?.heardFrom??'':'',
        defaultLocation:loaded.settings?.defaultLocation??
          ({'Princeton':'PR','West Chester':'WC','Yardley':'YD'}[
            loaded.defaultLocation??'']??loaded.defaultLocation??''),
        preferences:loaded.settings?.preferences??{},
        smsOptOut:loaded.settings?.smsOptOut??false});
      setStatus('ready');
    }).catch(()=>{
      if(!controller.signal.aborted)setStatus('error');
    });
    return ()=>controller.abort();
  },[staffId,customerId,locationCode]);


  // Team Member is ONE person. The profile may link to their independently
  // authorized booking customer ledger. Never derive identity from name/email.
  useEffect(()=>{
    const customerId=profile?.customerId;
    availableRequest.current?.abort();
    unavailableRequest.current?.abort();
    setAvailableCredits(null);setUnavailableCredits(null);
    setCreditAvailability('available');
    if(status!=='ready')return;
    if(!customerId){
      setAvailableCredits({state:'unlinked',data:null});
      return;
    }
    const controller=new AbortController();
    availableRequest.current=controller;
    setAvailableCredits({state:'loading',data:null});
    const params=new URLSearchParams({location:locationCode,availability:'available'});
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
      '/credits?'+params,{signal:controller.signal,credentials:'same-origin',
        cache:'no-store'}).then(async response=>{
      if(response.status===401||response.status===403){
        if(!controller.signal.aborted)setAvailableCredits({state:'denied',data:null});
        return;
      }
      if(!response.ok)throw Error('CREDITS_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:CreditPage};
      if(!body.ok||!validCreditPage(body.data,'available'))throw Error('INVALID_CREDITS');
      if(!controller.signal.aborted)setAvailableCredits({state:'ready',data:body.data});
    }).catch(()=>{
      if(!controller.signal.aborted)setAvailableCredits({state:'error',data:null});
    });
    return ()=>controller.abort();
  },[profile?.customerId,status,locationCode,creditRevision]);

  useEffect(()=>{
    unavailableRequest.current?.abort();
    setUnavailableCredits(null);
    if(status!=='ready'||tab!=='Service Credits'||
      creditAvailability!=='unavailable')return;
    const customerId=profile?.customerId;
    if(!customerId){
      setUnavailableCredits({state:'unlinked',data:null});
      return;
    }
    const controller=new AbortController();
    unavailableRequest.current=controller;
    setUnavailableCredits({state:'loading',data:null});
    const params=new URLSearchParams({location:locationCode,availability:'unavailable'});
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
      '/credits?'+params,{signal:controller.signal,credentials:'same-origin',
        cache:'no-store'}).then(async response=>{
      if(response.status===401||response.status===403){
        if(!controller.signal.aborted)setUnavailableCredits({state:'denied',data:null});
        return;
      }
      if(!response.ok)throw Error('CREDITS_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:CreditPage};
      if(!body.ok||!validCreditPage(body.data,'unavailable'))
        throw Error('INVALID_CREDITS');
      if(!controller.signal.aborted)setUnavailableCredits({state:'ready',data:body.data});
    }).catch(()=>{
      if(!controller.signal.aborted)setUnavailableCredits({state:'error',data:null});
    });
    return ()=>controller.abort();
  },[profile?.customerId,status,locationCode,tab,creditAvailability,creditRevision]);

  useEffect(()=>{
    historyRequest.current?.abort();
    const controller=new AbortController();
    historyRequest.current=controller;
    const customerId=profile?.customerId;
    setAppointmentHistory({state:'loading',items:[],hasMore:false,nextCursor:null});
    setHistoryBusy(false);setHistoryError('');
    if(status!=='ready')return ()=>controller.abort();
    if(!customerId){
      setAppointmentHistory({state:'unlinked',items:[],hasMore:false,nextCursor:null});
      return ()=>controller.abort();
    }
    const params=new URLSearchParams({location:locationCode,limit:'50'});
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
      '/appointments?'+params,{signal:controller.signal,
        credentials:'same-origin',cache:'no-store'}).then(async response=>{
      if(!response.ok)throw Error('HISTORY_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:HistoryPage};
      if(!body.ok||!validHistoryPage(body.data))throw Error('INVALID_HISTORY');
      if(!controller.signal.aborted)
        setAppointmentHistory({state:'ready',items:body.data.appointments,
          hasMore:body.data.hasMore,nextCursor:body.data.nextCursor});
    }).catch(()=>{
      if(!controller.signal.aborted)
        setAppointmentHistory({state:'error',items:[],hasMore:false,nextCursor:null});
    });
    return ()=>controller.abort();
  },[profile?.customerId,status,locationCode]);

  const loadOlderAppointments=async()=>{
    const cursor=appointmentHistory.nextCursor,customerId=profile?.customerId;
    if(!cursor||!customerId||historyBusy||appointmentHistory.state!=='ready')return;
    const controller=new AbortController();
    historyRequest.current?.abort();historyRequest.current=controller;
    setHistoryBusy(true);setHistoryError('');
    const params=new URLSearchParams({location:locationCode,limit:'50',
      beforeStart:cursor.startsAt,beforeId:cursor.appointmentId});
    try{
      const response=await fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
        '/appointments?'+params,{signal:controller.signal,
          credentials:'same-origin',cache:'no-store'});
      if(!response.ok)throw Error('HISTORY_UNAVAILABLE');
      const body=await response.json() as {ok:boolean;data?:HistoryPage};
      if(!body.ok||!validHistoryPage(body.data))
        throw Error('INVALID_HISTORY');
      if(controller.signal.aborted)return;
      setAppointmentHistory(previous=>previous.state==='ready'?{
        state:'ready',
        items:[...previous.items,...body.data!.appointments],
        hasMore:body.data!.hasMore,nextCursor:body.data!.nextCursor,
      }:previous);
    }catch{
      if(!controller.signal.aborted)setHistoryError('Could not load older appointments. Try again.');
    }finally{
      if(!controller.signal.aborted)setHistoryBusy(false);
    }
  };
  const creditDay=(expiresAt:string)=>{
    const parts=new Intl.DateTimeFormat('en-US',{
      timeZone:locationTimezone??'America/New_York',
      year:'numeric',month:'2-digit',day:'2-digit',
    }).formatToParts(new Date(Date.parse(expiresAt)-1000));
    const get=(type:string)=>parts.find(x=>x.type===type)?.value??'';
    return get('year')+'-'+get('month')+'-'+get('day');
  };
  const saveCreditExpiry=async(credit:ProfileCredit)=>{
    if(!profile?.customerId||creditExpiryBusy||
      (!creditNeverExpires&&!creditExpiresOn))return;
    setCreditExpiryBusy(true);setCreditExpiryStatus('');
    try{
      const response=await fetch('/api/v1/staff/customers/'+
        encodeURIComponent(profile.customerId)+'/credits/'+
        encodeURIComponent(credit.creditId)+'/expiration',{
          method:'PATCH',credentials:'same-origin',
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:locationCode,
            expectedExpiresAt:credit.expiresAt,
            expiresOn:creditNeverExpires?null:creditExpiresOn}),
        });
      const json=await response.json() as {ok:boolean;error?:{code:string}};
      if(!response.ok||!json.ok)
        throw Error(response.status===403?'Credit expiration edit access denied.':
          response.status===409?'Expiration changed. Refresh and retry.':
          json.error?.code??'Could not save credit expiration.');
      setCreditExpiryId(null);setCreditExpiryStatus('Credit expiration saved.');
      setCreditRevision(v=>v+1);
    }catch(error){
      setCreditExpiryStatus(error instanceof Error?error.message:
        'Could not save credit expiration.');
    }finally{setCreditExpiryBusy(false);}
  };
  const shownCredits=creditAvailability==='available'?availableCredits:unavailableCredits;
  const creditGroups=availableCredits?.state==='ready'&&availableCredits.data
    ?groupedCredits(availableCredits.data.credits):null;

  useEffect(()=>{
    if(!discountOpen)return;
    const controller=new AbortController();
    const timer=window.setTimeout(()=>{
      setDiscountStatus('Loading…');
      void fetch('/api/v1/staff/discounts?'+new URLSearchParams({
        location:locationCode,q:discountQuery.trim()}),{
        signal:controller.signal,credentials:'same-origin',cache:'no-store',
      }).then(async response=>{
        if(!response.ok)throw Error('Discount search unavailable.');
        const result=await response.json() as {ok:boolean;data?:{
          discounts:Array<{id:number;title:string;code:string;
            description:string;channel:string}>}};
        if(!result.ok||!result.data)throw Error('Discount search unavailable.');
        if(controller.signal.aborted)return;
        setDiscountChoices(result.data.discounts);
        setDiscountStatus(result.data.discounts.length?'':'No matching discount codes.');
      }).catch(error=>{
        if(!controller.signal.aborted)setDiscountStatus(error instanceof Error?
          error.message:'Discount search unavailable.');
      });
    },200);
    return ()=>{controller.abort();window.clearTimeout(timer);};
  },[discountOpen,discountQuery,locationCode]);


  useEffect(()=>{
    if(!referrerOpen||referrerQuery.trim().length<2){
      setReferrerChoices([]);
      setReferrerStatus(referrerOpen?'Enter at least two characters.':'');
      return;
    }
    const controller=new AbortController();
    const timer=window.setTimeout(()=>{
      setReferrerStatus('Searching…');
      void fetch('/api/v1/staff/customers?'+new URLSearchParams({
        location:locationCode,q:referrerQuery.trim()}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async response=>{
        if(!response.ok)throw Error('Customer search unavailable.');
        const result=await response.json() as {ok:boolean;data?:{
          customers:Array<{customerId:string;displayName:string;
            homeLocationCode:string|null}>}};
        if(!result.ok||!result.data)throw Error('Customer search unavailable.');
        if(controller.signal.aborted)return;
        setReferrerChoices(result.data.customers);
        setReferrerStatus(result.data.customers.length?'':'No matching customers.');
      }).catch(error=>{
        if(!controller.signal.aborted)setReferrerStatus(error instanceof Error?
          error.message:'Customer search unavailable.');
      });
    },200);
    return ()=>{controller.abort();window.clearTimeout(timer);};
  },[referrerOpen,referrerQuery,locationCode]);

  useEffect(()=>{
    const id=customerId??profile?.customerId;
    if(!id)return;
    const controller=new AbortController();
    setWaiverStatus('loading');setWaiverTotalCount(0);
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(id)+'/waivers?'+
      new URLSearchParams({location:locationCode,limit:'50'}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Waivers unavailable');
      const data=await response.json() as {ok:boolean;data?:{
        waivers:typeof waiverHistory;totalCount:number;hasMore:boolean}};
      if(!data.ok||!data.data)throw Error('Waivers unavailable');
      if(!controller.signal.aborted){setWaiverHistory(data.data.waivers);
        setWaiverTotalCount(Number.isInteger(data.data.totalCount)?
          data.data.totalCount:data.data.waivers.length);
        setWaiverStatus('ready');}
    }).catch(()=>{if(!controller.signal.aborted)setWaiverStatus('error');});
    return ()=>controller.abort();
  },[customerId,profile?.customerId,locationCode]);

  const save=(patch:Record<string,unknown>)=>{
    if(!profile)return;
    setSaveStatus('Saving…');
    queue.current=queue.current.catch(()=>{}).then(async()=>{
      // One person profile. Emergency contacts are part of the same actor's
      // linked customer record regardless of which URL opened this page.
      const emergencyOnly=Object.keys(patch).length>0&&Object.keys(patch)
        .every(key=>['emergencyContactName','emergencyContactPhone',
          'emergencyContactRelationship'].includes(key));
      const contactCustomerId=customerId??
        (emergencyOnly?profile.customerId??null:null);
      if(contactCustomerId){
        const sidebarKeys=['defaultLocation','autoDiscountId',
          'singleDiscountId','referrerCustomerId'];
        if(Object.keys(patch).some(key=>sidebarKeys.includes(key))){
          const response=await fetch('/api/v1/staff/customers/'+
            encodeURIComponent(contactCustomerId)+'/sidebar',{
            method:'PATCH',credentials:'same-origin',cache:'no-store',
            headers:{'Content-Type':'application/json'},
            body:JSON.stringify({location:locationCode,
              expectedVersion:sidebarVersion.current,patch}),
          });
          const body=await response.json() as {ok:boolean;data?:{settings:Settings};
            error?:{code:string}};
          if(!response.ok||!body.ok||!body.data?.settings)
            throw Error(body.error?.code==='CUSTOMER_REFERRER_ALREADY_SET'
              ?'Referrer cannot be changed after assignment.':
              'Profile setting could not be saved.');
          sidebarVersion.current=body.data.settings.version;
          setProfile(previous=>previous?{...previous,settings:{
            ...previous.settings,...body.data!.settings,
            // Customer detail version belongs to its separate record.
            version:previous.settings.version,
          }}:previous);
          setSaveStatus('Saved');
          return;
        }
        const source=customerRecordRef.current;
        if(!source)throw Error('Profile is not ready.');
        const customerPatch:Record<string,unknown>={};
        if(typeof patch.displayName==='string')
          customerPatch.displayName=patch.displayName;
        if(typeof patch.contactEmail==='string')
          customerPatch.email=patch.contactEmail||null;
        if(typeof patch.phone==='string')
          customerPatch.phoneE164=patch.phone||null;
        if('streetAddress' in patch||'postalCode' in patch||
          'emergencyContactName' in patch||'emergencyContactPhone' in patch||
          'heardFrom' in patch||'emergencyContactRelationship' in patch){
          customerPatch.registrationDetails={
            address:patch.streetAddress===undefined?
              source.registrationDetails?.address??null:String(patch.streetAddress)||null,
            postalCode:patch.postalCode===undefined?
              source.registrationDetails?.postalCode??null:String(patch.postalCode)||null,
            emergencyContactName:patch.emergencyContactName===undefined?
              source.registrationDetails?.emergencyContactName??null:
              String(patch.emergencyContactName)||null,
            emergencyContactPhone:patch.emergencyContactPhone===undefined?
              source.registrationDetails?.emergencyContactPhone??null:
              String(patch.emergencyContactPhone)||null,
            emergencyContactRelationship:patch.emergencyContactRelationship===undefined?
              source.registrationDetails?.emergencyContactRelationship??null:
              String(patch.emergencyContactRelationship)||null,
            heardFrom:patch.heardFrom===undefined?
              source.registrationDetails?.heardFrom??null:
              String(patch.heardFrom)||null,
          };
        }
        if('birthday' in patch||'birthdayMonth' in patch||
          'birthdayDay' in patch||'birthdayYear' in patch){
          const month=Number(patch.birthdayMonth??source.birthdayMonth??0);
          const day=Number(patch.birthdayDay??source.birthdayDay??0);
          const year=Number(patch.birthdayYear??source.birthdayYear??0);
          if((month&&!day)||(!month&&day))
            throw Error('Birthday needs both month and day.');
          customerPatch.birthday={month:month||null,day:day||null,year:year||null};
        }
        if(patch.preferences){
          const changes=patch.preferences as Record<string,boolean>;
          const names:Record<string,string>={
            mailingList:'mailingList',nonEssentialEmails:'nonEssential',
            saleReceipts:'saleReceipt',emailConfirmations:'appointmentConfirmation',
            emailReminders:'appointmentReminder',
          };
          if(typeof changes.textAppointmentReminders==='boolean')
            customerPatch.smsAppointmentReminders=changes.textAppointmentReminders;
          // Staff-profile choices can predate the unified customer
          // registration. A single checkbox edit must not erase the other
          // already-saved actor preferences when registration has no snapshot.
          const legacy=source.sidebarSettings?.preferences;
          const inherited=source.emailPreferences??{
            appointmentConfirmation:legacy?.emailConfirmations===true,
            appointmentReminder:legacy?.emailReminders===true,
            saleReceipt:legacy?.saleReceipts===true,
            nonEssential:legacy?.nonEssentialEmails===true,
            mailingList:legacy?.mailingList===true,
          };
          const merged:Record<string,boolean>={...inherited};
          for(const [key,value] of Object.entries(changes)){
            if(names[key])merged[names[key]]=value;
          }
          customerPatch.emailPreferences=merged;
        }
        // An ordinary customer must never silently gain employee privileges.
        // Only customer record fields supported by its authorized API are
        // posted here. Permissions use an independently owner-authorized flow.
        if(patch.smsOptOut===true){
          customerPatch.smsConsent={status:'OPTED_OUT',source:'IN_PERSON'};
          customerPatch.smsAppointmentReminders=false;
        }
        if(!Object.keys(customerPatch).length)
          throw Error('This setting is not yet supported for customer profiles.');
        const response=await fetch('/api/v1/staff/customers/'+
          encodeURIComponent(contactCustomerId),{
          method:'PATCH',credentials:'same-origin',cache:'no-store',
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:locationCode,
            expectedVersion:source.version,
            idempotencyKey:crypto.randomUUID()+'-customer-profile',
            patch:customerPatch}),
        });
        const body=await response.json() as {ok:boolean;data?:{version:number};
          error?:{code?:string}};
        if(!response.ok||!body.ok||!body.data?.version)
          throw Error(body.error?.code==='CUSTOMER_CHANGED'
            ?'Profile updated elsewhere. Reload before editing.':
            'Change could not be saved.');
        if(customerId)version.current=body.data.version;
        const updatedCustomer:CustomerProfileRecord={
          ...source,version:body.data!.version,
          displayName:String(customerPatch.displayName??source.displayName),
          email:customerPatch.email===undefined?source.email:
            customerPatch.email as string|null,
          phoneE164:customerPatch.phoneE164===undefined?source.phoneE164:
            customerPatch.phoneE164 as string|null,
          registrationDetails:customerPatch.registrationDetails===undefined
            ?source.registrationDetails:customerPatch.registrationDetails as
              CustomerProfileRecord['registrationDetails'],
          emailPreferences:customerPatch.emailPreferences===undefined
            ?source.emailPreferences:customerPatch.emailPreferences as
              CustomerProfileRecord['emailPreferences'],
          smsAppointmentReminders:customerPatch.smsAppointmentReminders===undefined
            ?source.smsAppointmentReminders:
              customerPatch.smsAppointmentReminders as boolean,
          ...('birthday' in customerPatch?{
            birthdayMonth:(customerPatch.birthday as {month:number|null}).month,
            birthdayDay:(customerPatch.birthday as {day:number|null}).day,
            birthdayYear:(customerPatch.birthday as {year:number|null}).year,
          }:{}),
        };
        customerRecordRef.current=updatedCustomer;
        setCustomerRecord(updatedCustomer);
        setProfile(previous=>previous?{
          ...previous,displayName:String(customerPatch.displayName??previous.displayName),
          email:customerPatch.email===undefined?previous.email:
            customerPatch.email as string|null,
          phone:customerPatch.phoneE164===undefined?previous.phone:
            customerPatch.phoneE164 as string|null,
          settings:{...previous.settings,
            version:customerId?body.data!.version:previous.settings.version,
            ...(typeof patch.streetAddress==='string'
              ?{streetAddress:patch.streetAddress}:{}),
            ...(typeof patch.postalCode==='string'
              ?{postalCode:patch.postalCode}:{}),
            ...(typeof patch.birthday==='string'||patch.birthday===null
              ?{birthday:patch.birthday as string|null}:{}),
            ...('preferences' in patch
              ?{preferences:{...previous.settings.preferences,
                ...patch.preferences as Record<string,boolean>}}:{})},
        }:previous);
        setSaveStatus('Saved');
        return;
      }
      const response=await fetch('/api/v1/staff/team/'+encodeURIComponent(staffId)+'/profile',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,expectedVersion:version.current,patch}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        settings:Settings;displayName?:string};error?:{code:string}};
      if(!response.ok||!body.ok||!body.data?.settings)
        throw new Error(body.error?.code==='PROFILE_CHANGED'?
          'Profile updated elsewhere. Reload before editing.':'Change could not be saved.');
      const result=body.data;
      version.current=result.settings.version;
      setProfile(previous=>previous?{...previous,
        ...(result.displayName?{displayName:result.displayName}:{}),
        ...(typeof patch.contactEmail==='string'?{email:patch.contactEmail}:{}),
        ...(typeof patch.phone==='string'?{phone:patch.phone}:{}),
        ...(typeof patch.defaultLocation==='string'?{defaultLocation:patch.defaultLocation}:{}),
        settings:result.settings}:previous);
      // Each checkbox updates its local state immediately. Saving another
      // queued field must NEVER replay an older server snapshot over newer
      // toggles the Team Member has already made.
      // Initial profile load is the only full draft synchronization.
      if(typeof patch.smsOptOut==='boolean')
        setDraft(previous=>({...previous,smsOptOut:patch.smsOptOut as boolean,
          preferences:patch.smsOptOut===true?
            {...previous.preferences,textAppointmentReminders:false}:
            previous.preferences}));
      setSaveStatus('Saved');
    }).catch(error=>setSaveStatus(error instanceof Error?
      error.message:'Change could not be saved.'));
    const key=customerId?'customer:'+customerId:'staff:'+staffId;
    const pending=queue.current;
    pendingProfileSaves.set(key,pending);
    void pending.finally(()=>{
      if(pendingProfileSaves.get(key)===pending)
        pendingProfileSaves.delete(key);
    });
  };
  // Employee profiles still use their linked booking customer record.
  // The status control is the SAME one on every person's profile.
  useEffect(()=>{
    if(customerId||!profile?.customerId||status!=='ready')return;
    const controller=new AbortController();
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(profile.customerId)+
      '?'+new URLSearchParams({location:locationCode}),{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)return;
      const body=await response.json() as {ok:boolean;data?:{profile:CustomerProfileRecord}};
      if(!controller.signal.aborted&&body.ok&&body.data?.profile){
        setCustomerRecord(body.data.profile);
        customerRecordRef.current=body.data.profile;
        // Keep the existing inputs hydrated on this same profile.
        const saved=body.data.profile.registrationDetails;
        setDraft(previous=>({...previous,
          emergencyContactName:saved?.emergencyContactName??'',
          emergencyContactPhone:saved?.emergencyContactPhone??'',
          emergencyContactRelationship:saved?.emergencyContactRelationship??'',
        }));
      }
    }).catch(()=>{});
    return ()=>controller.abort();
  },[customerId,profile?.customerId,locationCode,status]);
  const changeAccountStatus=async()=>{
    if(!canManageTeam||accountStatusBusy||!customerRecord)return;
    const next=customerRecord.status==='DISABLED'?'ACTIVE':'DISABLED';
    setAccountStatusBusy(true);setAccountStatusNotice('');
    try{
      const response=await fetch('/api/v1/staff/customers/'+
        encodeURIComponent(customerRecord.customerId)+'/status',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,status:next,
          expectedVersion:customerRecord.version}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        status:'ACTIVE'|'DISABLED';version:number;customerId:string};
        error?:{code?:string}};
      if(!response.ok||!body.ok||!body.data||
        body.data.customerId!==customerRecord.customerId)
        throw Error(body.error?.code??'Account status could not be changed.');
      setCustomerRecord(previous=>previous?{
        ...previous,status:body.data!.status,version:body.data!.version}:previous);
      version.current=body.data.version;
      setAccountStatusNotice(body.data.status==='DISABLED'?
        'Account disabled.':'Account enabled.');
    }catch(e){setAccountStatusNotice(e instanceof Error?e.message:
      'Account status could not be changed.');}
    finally{setAccountStatusBusy(false);}
  };
  const enableEmployeeAccess=async()=>{
    if(!customerId||!customerRecord?.actorId||!canManageTeam||
      customerRecord.actorKind==='TEAM_MEMBER'||employmentBusy)return;
    setEmploymentBusy(true);setEmploymentStatus('Saving permissions…');
    try{
      const response=await fetch('/api/v1/staff/users/'+
        encodeURIComponent(customerId)+'/employee-access',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,enabled:true}),
      });
      const result=await response.json() as {
        ok:boolean;data?:{actorId:string;customerId:string;isEmployee:true};
        error?:{code:string}};
      if(!response.ok||!result.ok||!result.data||
        result.data.actorId!==customerRecord.actorId||
        result.data.customerId!==customerId||
        result.data.isEmployee!==true)
        throw Error(result.error?.code??'Permissions could not be saved.');
      setCustomerRecord(previous=>previous?{
        ...previous,actorKind:'TEAM_MEMBER'}:previous);
      setEmploymentStatus('Employee permissions enabled for this same account.');
    }catch(error){
      setEmploymentStatus(error instanceof Error?
        error.message:'Permissions could not be saved.');
    }finally{setEmploymentBusy(false);}
  };
  const updateField=(key:'contactEmail'|'phone'|'streetAddress'|'postalCode'|
    'birthday'|'emergencyContactName'|'emergencyContactPhone'|
    'emergencyContactRelationship'|'heardFrom',
    value:string)=>{
    const old=key==='contactEmail'?profile?.email:key==='phone'?profile?.phone:
      key==='emergencyContactName'||key==='emergencyContactPhone'||
      key==='emergencyContactRelationship'||key==='heardFrom'?
        customerRecord?.registrationDetails?.[key]:profile?.settings?.[key];
    if(value===(old??''))return;
    save({[key]:key==='birthday'?value||null:value});
  };
  const saveCustomerBirthday=(patch:Record<string,unknown>)=>{
    if(!customerId||!customerRecord)return;
    const month=Number(patch.birthdayMonth??draft.birthdayMonth??0);
    const day=Number(patch.birthdayDay??draft.birthdayDay??0);
    if((month&&!day)||(!month&&day))return;
    save({
      birthdayMonth:month?String(month):'',
      birthdayDay:day?String(day):'',
      birthdayYear:String(patch.birthdayYear??draft.birthdayYear??''),
    });
  };
  const commitName=()=>{
    const name=(draft.firstName+' '+draft.lastName).trim().replace(/\s+/g,' ');
    if(name.length>=2&&name!==profile?.displayName)save({displayName:name});
  };
  const openHistory=(suffix:'communications'|'waiver_history'|'tasks/completed')=>{
    const base=profile?.helmUserId?'/users/'+encodeURIComponent(profile.helmUserId):
      '/team/'+encodeURIComponent(staffId);
    window.open(base+'/'+suffix,'ecfs-user-history-'+suffix.replace('/','-'),
      suffix==='tasks/completed'?'width=1100,height=740':'width=520,height=570');
  };
  const displayName=profile?.displayName??initial?.displayName??'';
  const input=(label:string,key:'firstName'|'lastName'|'contactEmail'|'phone'|
    'streetAddress'|'postalCode'|'birthday'|'emergencyContactName'|
    'emergencyContactPhone'|'emergencyContactRelationship'|'heardFrom',type='text')=>
    <label className="ecfs-helm-contact-field">
      <input aria-label={label} className="ecfs-profile-edit-field"
        type={type} placeholder={label} value={draft[key]}
        maxLength={key==='streetAddress'?300:key==='contactEmail'?320:120}
        onChange={event=>setDraft(previous=>({...previous,[key]:event.target.value}))}
        onBlur={()=>key==='firstName'||key==='lastName'?commitName():updateField(key,draft[key])}/>
    </label>;

  return <article className="ecfs-record-profile" aria-label="Profile page">
    <aside className="ecfs-record-sidebar" aria-label="Profile sidebar">
      <ProfileAvatar actorId={profile?.staffId??staffId} displayName={displayName} lastLoginAt={profile?.lastLoginAt}/>
      <div className="ecfs-helm-side-statistics">
        {customerId?<span>Communications</span>:
          <button type="button" className="ecfs-profile-inline-link"
            onClick={()=>openHistory('communications')}>Communications</button>}
        <button type="button" className="ecfs-profile-inline-link"
          onClick={()=>(customerId??profile?.customerId)
            ?setWaiversOpen(true):openHistory('waiver_history')}>
          {waiverStatus==='ready'?`${waiverTotalCount} `:''}Waivers</button>
        <span>Past Visits</span>
        <span>Cancellations</span>
        <span>No Shows</span>
        <span>Spent</span>
        {!customerId&&<button type="button" className="ecfs-profile-inline-link"
          onClick={()=>openHistory('tasks/completed')}>Completed Tasks</button>}
      </div>
      <div className="ecfs-helm-side-link">Set Pre-Pay Rule</div>
      <div className="ecfs-helm-side-link">
        {profile?.settings?.referrerCustomerId?
          <span>Referred by {profile.settings.referrerName??'Customer'}</span>:
          <button type="button" className="ecfs-profile-inline-link"
            onClick={()=>{setReferrerOpen(x=>!x);setReferrerQuery('');}}>
            No Referrer</button>}
      </div>
      {referrerOpen&&<div role="dialog" aria-label="Find Referrer"
        className="ecfs-profile-discount-dialog">
        <strong>Find Referrer</strong>
        <input type="search" aria-label="Search referrer"
          value={referrerQuery} placeholder="Search customers"
          onChange={event=>setReferrerQuery(event.target.value)}/>
        <div className="ecfs-profile-discount-results">
          {referrerChoices.map(customer=><button type="button" key={customer.customerId}
            onClick={()=>{
              save({referrerCustomerId:customer.customerId});
              setReferrerOpen(false);
            }}>
            <strong>{customer.displayName}</strong>
            <small>{customer.homeLocationCode??'Unassigned'}</small>
          </button>)}
        </div>
        {referrerStatus&&<small role="status">{referrerStatus}</small>}
        {!profile?.settings?.referrerCustomerId&&
          <span className="ecfs-profile-referrer-help">Select a customer to set the referrer.</span>}
        <button type="button" onClick={()=>setReferrerOpen(false)}>Cancel</button>
      </div>}

      <section className="ecfs-record-side-box ecfs-helm-referral">
        <strong>Referral Programs</strong>
        <CustomerReferralProgramsPanel
          staffId={profile?.staffId??staffId}
          customerId={customerId??profile?.customerId??null}
          locationCode={locationCode}/>
      </section>
      <section className="ecfs-record-side-box">
        <strong>Auto Discount</strong>
        <button type="button" className="ecfs-profile-inline-link"
          onClick={()=>{setDiscountOpen('auto');setDiscountQuery('');}}>
          {profile?.settings?.autoDiscountTitle??'Choose discount'}</button>
        <strong>Single-use Auto Discount</strong>
        <button type="button" className="ecfs-profile-inline-link"
          onClick={()=>{setDiscountOpen('single');setDiscountQuery('');}}>
          {profile?.settings?.singleDiscountTitle??'Choose discount'}</button>
      </section>
      {discountOpen&&<div className="ecfs-profile-discount-dialog" role="dialog"
        aria-label={discountOpen==='auto'?'Auto Discount search':'Single-use Auto Discount search'}>
        <strong>{discountOpen==='auto'?'Auto Discount':'Single-use Auto Discount'}</strong>
        <input type="search" aria-label="Search discount codes"
          placeholder="Search code or description" value={discountQuery}
          onChange={event=>setDiscountQuery(event.target.value)}/>
        <div className="ecfs-profile-discount-results">
          {discountChoices.map(code=><button type="button" key={code.id}
            onClick={()=>{
              save({[discountOpen==='auto'?'autoDiscountId':'singleDiscountId']:code.id});
              setDiscountOpen(null);
            }}>
            <strong>{code.title}</strong><small>{code.code}</small>
            <small>{code.description}</small>
          </button>)}
        </div>
        {discountStatus&&<small role="status">{discountStatus}</small>}
        <button type="button" onClick={()=>{
          save({[discountOpen==='auto'?'autoDiscountId':'singleDiscountId']:null});
          setDiscountOpen(null);
        }}>Remove discount</button>
        <button type="button" onClick={()=>setDiscountOpen(null)}>Cancel</button>
      </div>}
      <section className="ecfs-record-side-box">
        <strong>Default Location</strong>
        <select aria-label="Default Location" value={draft.defaultLocation}
          onChange={event=>{
            setDraft(previous=>({...previous,defaultLocation:event.target.value}));
            save({defaultLocation:event.target.value||null});
          }}>
          <option value="">Choose location</option>
          {(['WC','PR','YD'] as const)
            .map(code=><option key={code} value={code}>
              {locationTitle[code]??code}
            </option>)}
        </select>
      </section>

      <button type="button" className="ecfs-helm-begin-sale"
        disabled={beginSaleBusy} onClick={()=>{
          if(beginSaleBusy)return;
          setBeginSaleBusy(true);setBeginSaleError('');
          void (async()=>{
            try{
              const id=customerId??profile?.customerId;
              if(id){onBeginSale({customerId:id,displayName});return;}
              const response=await fetch(
                '/api/v1/staff/team/'+encodeURIComponent(staffId)+'/sale-customer',{
                  method:'POST',credentials:'same-origin',cache:'no-store',
                  headers:{'content-type':'application/json'},
                  body:JSON.stringify({location:locationCode}),
                });
              const body=await response.json() as {ok:boolean;
                data?:{customerId:string;displayName:string};error?:{code:string}};
              if(!response.ok||!body.ok||!body.data?.customerId)
                throw Error('Unable to start a sale for this person.');
              onBeginSale(body.data);
            }catch(e){setBeginSaleError(e instanceof Error?e.message:
              'Unable to start a sale for this person.');
            }finally{setBeginSaleBusy(false);}
          })();
        }}>{beginSaleBusy?'Opening Sale…':'Begin Sale'}</button>
      {beginSaleError&&<small role="alert">{beginSaleError}</small>}



      <div className="ecfs-helm-contact-fields" aria-label="Contact details">
        {input('First Name','firstName')}
        {input('Last Name','lastName')}
        {input('Phone','phone','tel')}
        {input('Email','contactEmail','email')}
        {input('Address','streetAddress')}
        {input('Postal Code','postalCode')}
        {!customerId?input('Birthday','birthday','date'):
          <div className="ecfs-profile-birthday" aria-label="Birthday">
            {!birthdayEditing?
              <button type="button" aria-label="Edit Birthday"
                className="ecfs-profile-edit-field ecfs-birthday-link"
                onClick={()=>setBirthdayEditing(true)}>
                {draft.birthdayMonth&&draft.birthdayDay
                  ?new Intl.DateTimeFormat('en-US',{month:'long',day:'numeric',
                    ...(draft.birthdayYear?{year:'numeric' as const}:{})})
                    .format(new Date(Date.UTC(
                      Number(draft.birthdayYear)||2000,
                      Number(draft.birthdayMonth)-1,Number(draft.birthdayDay))))
                  :'Birthday'}
              </button>:
              <div className="ecfs-profile-birthday-edit">
                <select aria-label="Profile birthday month" value={draft.birthdayMonth}
                  onChange={event=>setDraft(current=>({
                    ...current,birthdayMonth:event.target.value}))}>
                  <option value="">month</option>
                  {Array.from({length:12},(_,i)=><option key={i+1} value={i+1}>
                    {new Intl.DateTimeFormat('en-US',{month:'long'})
                      .format(new Date(Date.UTC(2024,i,1)))}
                  </option>)}
                </select>
                <input aria-label="Profile birthday day" type="number" min={1}
                  max={31} placeholder="day" value={draft.birthdayDay}
                  onChange={event=>setDraft(current=>({
                    ...current,birthdayDay:event.target.value}))}/>
                <input aria-label="Profile birthday year" type="number"
                  min={1900} max={2100} placeholder="year"
                  value={draft.birthdayYear} onChange={event=>setDraft(current=>({
                    ...current,birthdayYear:event.target.value}))}/>
                <button type="button" onClick={()=>{
                  saveCustomerBirthday({});setBirthdayEditing(false);
                }}>Save</button>
              </div>}
          </div>}
      </div>
      <div className="ecfs-helm-side-link">
        {viewerActorId===(profile?.staffId??staffId)?<ChangePasswordPanel/>:
          <button type="button" className="ecfs-profile-inline-link"
            onClick={()=>setPasswordOpen(x=>!x)}>Change Password</button>}
        {passwordOpen&&viewerActorId!==staffId&&
          <p role="status">Only the account holder can change their password.
            Administrator-managed password changes are not connected.</p>}
      </div>
      <div className="ecfs-helm-communications" aria-label="Communication preferences">
        {preferenceLabels.map(([key,label])=>
          <label key={key} className="ecfs-profile-preference">
            <input type="checkbox" checked={draft.preferences[key]===true}
              disabled={key==='textAppointmentReminders'&&draft.smsOptOut}
              onChange={event=>{
                const value=event.target.checked;
                setDraft(previous=>({...previous,preferences:{
                  ...previous.preferences,[key]:value}}));
                save({preferences:{[key]:value}});
              }}/>
            {label}
          </label>)}
        <button type="button" className="ecfs-profile-inline-link"
          onClick={()=>setOptOutOpen(x=>!x)}>Opt-out of all Text Messaging</button>
        {optOutOpen&&<div className="ecfs-profile-optout-confirm">
          <button type="button" onClick={()=>{
            setDraft(previous=>({...previous,smsOptOut:true,preferences:{
              ...previous.preferences,textAppointmentReminders:false}}));
            save({smsOptOut:true});setOptOutOpen(false);
          }}>Text Msg Opt-Out</button>
          <button type="button" onClick={()=>setOptOutOpen(false)}>Cancel</button>
        </div>}
        {draft.smsOptOut&&<small>Text Msg Opt-Out is enabled.</small>}
      </div>
      {saveStatus&&<small role="status" className="ecfs-profile-save-status">
        {saveStatus}</small>}
      <div className="ecfs-helm-emergency">
        <strong>Emergency Contact</strong>
        {(customerId||profile?.customerId)?<>
          {input('Emergency Contact Name','emergencyContactName')}
          {input('Emergency Contact Phone','emergencyContactPhone','tel')}
          {input('Emergency Contact Relationship','emergencyContactRelationship')}
        </>:<>
          <div>Phone</div><div>Relationship</div>
        </>}
      </div>
      <ProfileSavedCards
        customerId={customerId??profile?.customerId??customerRecord?.customerId??null}
        staffId={customerId?null:profile?.staffId??staffId} location={locationCode}/>
      {customerRecord&&<div className="ecfs-person-account-status">
        <small>Account Status: {customerRecord.status==='DISABLED'?'Disabled':'Active'}</small>
        {canManageTeam&&<button type="button" className="ecfs-helm-account-disable"
          disabled={accountStatusBusy} onClick={()=>void changeAccountStatus()}>
          {accountStatusBusy?'Saving…':customerRecord.status==='DISABLED'
            ?'enable account':'disable account'}
        </button>}
        {accountStatusNotice&&<small role="status">{accountStatusNotice}</small>}
      </div>}
    </aside>

    {waiversOpen&&<div className="ecfs-profile-waiver-backdrop"
      role="presentation" onClick={()=>setWaiversOpen(false)}>
      <section className="ecfs-profile-waiver-popup" role="dialog"
        aria-modal="true" aria-label={displayName+' Waiver History'}
        onClick={event=>event.stopPropagation()}>
        <button type="button" className="ecfs-profile-waiver-close"
          onClick={()=>setWaiversOpen(false)}>Close</button>
        <h2>{displayName} Waiver History</h2>
        {waiverStatus==='loading'&&<p role="status">Loading waiver history…</p>}
        {waiverStatus==='error'&&<p role="alert">Waiver history unavailable.</p>}
        {waiverStatus==='ready'&&(waiverHistory.length?
          <table><thead><tr><th>Signed</th><th>Waiver</th><th>Version</th>
          </tr></thead><tbody>{waiverHistory.map(waiver=><tr
            key={waiver.acceptanceId}>
            <td>{new Date(waiver.signedAt).toLocaleDateString('en-US')}</td>
            <td>{waiver.title}</td><td>{waiver.revision}</td>
          </tr>)}</tbody></table>:
          <p>No waiver history.</p>)}
        {waiverStatus==='ready'&&waiverTotalCount>waiverHistory.length&&
          <p>Showing the latest {waiverHistory.length} of {waiverTotalCount} signed waivers.</p>}
      </section>
    </div>}
    <div className="ecfs-record-main">
      {customerId&&<StaffSalePaymentReturn customerId={customerId}/>}
      {customerId&&<StaffMembershipPaymentReturn customerId={customerId}/>}
      {customerId&&<StaffGiftCardPaymentReturn customerId={customerId}/>}
      <div className="ecfs-record-heading">
        <label className="ecfs-employee-color" title="Shift color">
          <input type="color" aria-label="Employee shift color"
            value={profile?.settings?.shiftColor??'#9c9c9c'}
            disabled={status!=='ready'||!!customerId}
            onChange={event=>save({shiftColor:event.target.value})}/>
        </label>
        <h1>{displayName}</h1>
        <button type="button" className="ecfs-record-back" onClick={onBack}>
          Back to Schedule
        </button>
      </div>

      <div className="ecfs-record-top-detail" role="region"
        aria-label="Team Member available service credits">
        <span className="ecfs-record-label">Available Service Credits</span>
        {status==='ready'&&availableCredits?.state==='loading'&&
          <p role="status">Loading service credits…</p>}
        {status==='ready'&&availableCredits?.state==='denied'&&
          <p role="alert">Service credit access is not permitted.</p>}
        {status==='ready'&&availableCredits?.state==='error'&&
          <p role="alert">Service credit records are unavailable.</p>}
        {status==='ready'&&availableCredits?.state==='unlinked'&&
          <p role="status">No service credits recorded for this person.</p>}
        {creditGroups&&(creditGroups.length?
          <ul className="ecfs-record-credit-list">
            {creditGroups.map(item=><li key={item.code}>
              <span>{item.name}</span><strong>{item.quantity}</strong>
            </li>)}
          </ul>:
          <p role="status">No available service credits recorded.</p>)}
      </div>

      <nav className="ecfs-record-tabs" role="tablist" aria-label="Profile tabs">
        {profileTabs.filter(item=>!customerId||
          customerRecord?.actorKind==='TEAM_MEMBER'||
          !['Shifts','Tasks'].includes(item)).map(item=><button type="button" key={item}
          role="tab" aria-selected={tab===item}
          className={tab===item?'is-active':''}
          onClick={()=>setTab(item)}>{item}</button>)}
      </nav>
      <section className="ecfs-record-tab-content" role="tabpanel" aria-label={tab}>
        {status==='loading'&&<p role="status">Loading profile…</p>}
        {status==='error'&&<p role="alert">Profile information is unavailable.</p>}
        {status==='ready'&&tab==='Appointments'&&<>
          <div className="ecfs-record-filterbar">
            <label>Appointments <select aria-label="Appointment status"
              value={appointmentStatusFilter}
              onChange={event=>setAppointmentStatusFilter(event.target.value)}>
              <option value="All">All</option>
              {[...new Set(appointmentHistory.items.map(item=>item.status))].sort().map(status=>
                <option key={status} value={status}>{status.replaceAll('_',' ')}</option>)}
            </select></label>
          </div>
          <div className="ecfs-record-table-scroll">
            <table className="ecfs-record-table">
              <thead><tr>
                <th>Beginning</th><th>Service</th><th>Room</th><th>Status</th>
              </tr></thead>
              <tbody>
                {appointmentHistory.state==='loading'&&
                  <tr><td colSpan={4}>Loading appointments…</td></tr>}
                {appointmentHistory.state==='error'&&
                  <tr><td colSpan={4}>Appointment history is currently unavailable.</td></tr>}
                {appointmentHistory.state==='ready'&&
                  (appointmentHistory.items.filter(item=>appointmentStatusFilter==='All'||
                    item.status===appointmentStatusFilter).length
                  ?appointmentHistory.items.filter(item=>appointmentStatusFilter==='All'||
                    item.status===appointmentStatusFilter).map(item=>
                    <tr key={item.appointmentId}>
                      <td>{new Intl.DateTimeFormat('en-US',{
                        timeZone:item.locationTimezone,year:'numeric',month:'short',
                        day:'numeric',hour:'numeric',minute:'2-digit',
                      }).format(new Date(item.startsAt))}</td>
                      <td>{item.serviceName}</td>
                      <td>{item.resourceName}</td>
                      <td>{item.status.replaceAll('_',' ')}</td>
                    </tr>):
                    <tr><td colSpan={4}>No appointments recorded for this person.</td></tr>)}
              </tbody>
            </table>
            {appointmentHistory.state==='unlinked'&&
              <p role="status">No appointments recorded for this person.</p>}
            {historyError&&<p role="alert">{historyError}</p>}
            {appointmentHistory.state==='ready'&&appointmentHistory.hasMore&&
              <button type="button" disabled={historyBusy}
                onClick={()=>void loadOlderAppointments()}>
                {historyBusy?'Loading older appointments…':'Load older appointments'}
              </button>}
          </div>
        </>}
        {status==='ready'&&tab==='Purchases'&&
          <CustomerPurchasesPanel
            customerId={customerId??profile?.customerId??null}
            location={locationCode}/>}
        {status==='ready'&&tab==='Service Credits'&&
          <section className="ecfs-record-credit-detail" role="region"
            aria-label="Team Member service credits">
            <div role="group" aria-label="Service credit availability">
              <button type="button" aria-pressed={creditAvailability==='available'}
                onClick={()=>setCreditAvailability('available')}>Available</button>
              <button type="button" aria-pressed={creditAvailability==='unavailable'}
                onClick={()=>setCreditAvailability('unavailable')}>Unavailable</button>
            </div>
            {creditExpiryStatus&&<p role="status">{creditExpiryStatus}</p>}
            {shownCredits?.state==='loading'&&<p role="status">Loading service credits…</p>}
            {shownCredits?.state==='denied'&&
              <p role="alert">Service credit access is not permitted.</p>}
            {shownCredits?.state==='error'&&
              <p role="alert">Service credit records are unavailable.</p>}
            {shownCredits?.state==='unlinked'&&
              <p role="status">No service credits recorded for this person.</p>}
            {shownCredits?.state==='ready'&&shownCredits.data&&(
              shownCredits.data.credits.length?
                <div className="ecfs-record-table-scroll"><table className="ecfs-record-table">
                  <thead><tr><th>Service</th><th>Remaining</th><th>Issued</th>
                    <th>Expiration</th></tr></thead>
                  <tbody>{shownCredits.data.credits.map(credit=><tr key={credit.creditId}>
                    <td>{credit.serviceName}</td>
                    <td>{credit.remainingUses}</td>
                    <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',
                      timeZone:'UTC'}).format(new Date(credit.issuedAt))}</td>
                    <td>
                      <button type="button"
                        aria-label={'Edit expiration for '+credit.serviceName}
                        disabled={creditExpiryBusy}
                        onClick={()=>{
                          setCreditExpiryId(credit.creditId);
                          setCreditNeverExpires(credit.expiresAt===null);
                          setCreditExpiresOn(credit.expiresAt?creditDay(credit.expiresAt):'');
                          setCreditExpiryStatus('');
                        }}>
                        {credit.expiresAt?creditDay(credit.expiresAt):'No expiration'}
                      </button>
                      {creditExpiryId===credit.creditId&&
                        <div className="ecfs-credit-expiration-editor">
                          <label><input type="checkbox" checked={creditNeverExpires}
                            disabled={creditExpiryBusy}
                            onChange={ev=>setCreditNeverExpires(ev.target.checked)}/>
                            No expiration</label>
                          {!creditNeverExpires&&<label>Expiration date
                            <input type="date" aria-label="Credit expiration date"
                              disabled={creditExpiryBusy} required
                              value={creditExpiresOn}
                              onChange={ev=>setCreditExpiresOn(ev.target.value)}/>
                          </label>}
                          <button type="button" disabled={creditExpiryBusy||
                            (!creditNeverExpires&&!creditExpiresOn)}
                            onClick={()=>void saveCreditExpiry(credit)}>
                            {creditExpiryBusy?'Saving…':'Save'}
                          </button>
                          <button type="button" disabled={creditExpiryBusy}
                            onClick={()=>setCreditExpiryId(null)}>Cancel</button>
                        </div>}
                    </td>
                  </tr>)}</tbody>
                </table></div>:
                <p role="status">No {creditAvailability} service credits recorded.</p>)}
          </section>}
        {status==='ready'&&tab==='Permissions'&&<>
          {customerId&&customerRecord&&<section className="ecfs-person-permissions"
            aria-label="User employee permissions">
            <label>
              <input type="checkbox" aria-label="Is Shop Employee"
                checked={customerRecord.actorKind==='TEAM_MEMBER'}
                disabled={!canManageTeam||employmentBusy||
                  customerRecord.actorKind==='TEAM_MEMBER'}
                onChange={event=>{
                  if(event.target.checked)void enableEmployeeAccess();
                }}/>
              Is Shop Employee
            </label>
            {employmentStatus&&<p role="status">{employmentStatus}</p>}
          </section>}
          {canManageTeam&&(!customerId||
            customerRecord?.actorKind==='TEAM_MEMBER')&&
            <><StaffLocationPermissions staffId={profile?.staffId??staffId}
              locationCode={locationCode}/>
              <AppSupportGrant actorId={profile?.staffId??staffId}
                locationCode={locationCode}/></>}
          <table className="ecfs-record-table">
            <thead><tr><th>Permission</th></tr></thead>
            <tbody>{profile?.permissions.length
              ?profile.permissions.map(permission=><tr key={permission}>
                <td>{permission}</td>
              </tr>)
              :<tr><td>No permissions recorded.</td></tr>}
            </tbody>
          </table></>}
        {status==='ready'&&tab!=='Appointments'&&tab!=='Permissions'&&
          tab!=='Service Credits'&&tab!=='Purchases'&&
          <div className="ecfs-record-no-data">
            {tab} functionality is not yet available in this PWA.
          </div>}
      </section>
    </div>
  </article>;
}
